import React, { useEffect, useRef, useState } from 'react';
import DialogShell from '../../components/ui/DialogShell';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import FormField from '../../components/ui/FormField';
import StatusBanner from '../../components/ui/StatusBanner';
import pilotDataPolicy from '../../shared/pilotDataPolicy.json';

const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_ATTR = ACCEPTED_MIME.join(',');

const DOCUMENT_TYPES = [
  { value: 'offer_letter', label: 'Offer letter' },
  { value: 'certification', label: 'Certification' },
  { value: 'other', label: 'Approved general employment document' }
].filter(option => pilotDataPolicy.documentTypes.includes(option.value));

function safeDefaultDocumentType(value) {
  return pilotDataPolicy.documentTypes.includes(value) ? value : 'other';
}

function formatBytes(n) {
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.ceil(n / 1024)} KB`;
}

export default function EmployeeDocumentUploadModal({
  open,
  onClose,
  defaultDocumentType = 'other',
  uploader,
  onUploaded,
  presentation = 'modal'
}) {
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState(safeDefaultDocumentType(defaultDocumentType));
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setDocumentType(safeDefaultDocumentType(defaultDocumentType));
      setExpiresAt('');
      setError('');
      setIsUploading(false);
    }
  }, [open, defaultDocumentType]);

  // Expiration is only meaningful for certifications. Clear it if
  // the user switches away from certification so a stale value
  // can't leak into a non-cert upload.
  useEffect(() => {
    if (documentType !== 'certification' && expiresAt) {
      setExpiresAt('');
    }
  }, [documentType, expiresAt]);

  const handleFile = (event) => {
    const picked = event.target.files?.[0];
    setError('');
    if (!picked) { setFile(null); return; }
    if (!ACCEPTED_MIME.includes(picked.type)) {
      setError('Document must be a PDF, JPEG, PNG, or WebP file.');
      setFile(null);
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError(`Document must be 10 MB or smaller. This file is ${formatBytes(picked.size)}.`);
      setFile(null);
      return;
    }
    setFile(picked);
  };

  const handleUpload = async () => {
    if (!file || !uploader) return;
    setIsUploading(true);
    setError('');
    try {
      const result = await uploader({
        file,
        documentType,
        expiresAt: documentType === 'certification' && expiresAt ? expiresAt : null
      });
      if (onUploaded) await onUploaded(result);
      onClose();
    } catch (err) {
      setError(err?.message || 'Unable to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DialogShell
      open={open}
      onClose={isUploading ? () => {} : onClose}
      title="Upload employee document"
      size="md"
      presentation={presentation}
      busy={isUploading}
      data-testid={presentation === 'drawer' ? 'employee-document-upload-drawer' : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isUploading}>Cancel</Button>
          <Button variant="primary" onClick={handleUpload} disabled={!file || isUploading}>
            {isUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </>
      }
    >
      {error ? <StatusBanner kind="error" message={error} /> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="Document type" htmlFor="document-type">
          <Select id="document-type" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
            {DOCUMENT_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </FormField>
        <p style={{ fontSize: 12, color: '#5b6471', margin: 0 }}>
          Pilot uploads are limited to offer letters, certifications, and approved low-sensitivity employment documents. Do not upload identity, tax, payroll, banking, medical, benefits, leave, or background-check records.
        </p>
        <FormField label="File" htmlFor="document-file" hint="PDF, JPEG, PNG, or WebP up to 10 MB.">
          <input
            ref={inputRef}
            id="document-file"
            type="file"
            accept={ACCEPT_ATTR}
            onChange={handleFile}
            data-testid="document-file-input"
          />
        </FormField>
        {documentType === 'certification' ? (
          <FormField
            label="Expires (optional)"
            htmlFor="document-expires-at"
            hint="Used to flag expiring certifications."
          >
            <Input
              id="document-expires-at"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="document-expires-at-input"
            />
          </FormField>
        ) : null}
        {file ? (
          <div style={{ fontSize: 13, color: '#1c2330' }}>
            <div style={{ fontWeight: 600 }}>{file.name}</div>
            <div style={{ color: '#5b6471', fontSize: 12 }}>
              {file.type} · {formatBytes(file.size)}
            </div>
          </div>
        ) : null}
        <p style={{ fontSize: 11, color: '#9ba2b1', margin: 0 }}>
          Uploads are tenant-scoped, audited, and deduplicated by file contents.
        </p>
      </div>
    </DialogShell>
  );
}
